/* eslint-disable max-classes-per-file */

import { $PropertyType } from "utility-types";
import type { UserSelf } from "storefront/User";
import "storefront/User";
import type { AuthenticationData } from "storefront/components/Authentication/Data";
import "storefront/components/Authentication/Data";
import { post } from "storefront/GrailedAPI/request";
import GrailedAPIError from "storefront/GrailedAPI/Error";
import {
  parseNextAction,
  TwoFactorChallenge,
} from "storefront/GrailedAPI/NextAction";

export type EmailCredentials = {
  identityProvider: "email";
  email: string;
  password: string;
  otp?: string;
  optInEmails?: boolean;
  recaptchaToken: string;
  recaptchaType: "invisible" | "checkbox";
};
export type IdentityCredentials = {
  identityToken: string;
  identityProvider: "google" | "facebook" | "apple";
  email: string | null | undefined;
};
export type OtpCredentials = {
  identityProvider: "email";
  email: string;
  password: string;
  otp: string;
  optInEmails?: boolean;
};
export type Credentials =
  | OtpCredentials
  | EmailCredentials
  | IdentityCredentials;
export type IdentityProvider = $PropertyType<Credentials, "identityProvider">;

const path: (arg0: Credentials) => string = (credentials) => {
  switch (credentials.identityProvider) {
    case "facebook":
      return "/api/sign_in/facebook";

    case "apple":
    case "google":
      return "/api/sign_in/oauth";

    case "email":
    default:
      return "/api/sign_in";
  }
};

type Response = {
  data: {
    // Only returned by /api/sign_in. The OAuth and Facebook endpoints always sign the user in.
    status?: "authenticated" | "two_factor_required";
    redirectTo: string;
    token: string | null;
    user: UserSelf | null;
    nextAction?: unknown;
  };
};

class UserNotFound extends Error {
  type: "UserNotFound" = "UserNotFound";

  credentials: Credentials;

  constructor(
    credentials: Credentials,
    message = "No account found for the given credentials.",
  ) {
    super(message);
    this.name = "UserNotFound";
    this.credentials = credentials;
  }
}
class OneTimePasswordRequired extends Error {
  type: "OneTimePasswordRequired" = "OneTimePasswordRequired";

  credentials: Credentials;

  constructor(
    credentials: Credentials,
    message = "Your one time password is required.",
  ) {
    super(message);
    this.name = "OneTimePasswordRequired";
    this.credentials = credentials;
  }
}
class Banned extends Error {
  type: "Banned" = "Banned";

  credentials: Credentials;

  constructor(credentials: Credentials, message = "That account is banned.") {
    super(message);
    this.name = "Banned";
    this.credentials = credentials;
  }
}
class InvalidCredentials extends Error {
  type: "InvalidCredentials" = "InvalidCredentials";

  credentials: Credentials;

  constructor(
    credentials: Credentials,
    message = "The given credentials are invalid.",
  ) {
    super(message);
    this.name = "InvalidCredentials";
    this.credentials = credentials;
  }
}
class NeedsPasswordReset extends Error {
  type: "NeedsPasswordReset" = "NeedsPasswordReset";

  credentials: Credentials;

  constructor(
    credentials: Credentials,
    message = "You need to reset your password.",
  ) {
    super(message);
    this.name = "NeedsPasswordReset";
    this.credentials = credentials;
  }
}
class ServiceUnavailable extends Error {
  type: "ServiceUnavailable" = "ServiceUnavailable";

  credentials: Credentials;

  constructor(
    credentials: Credentials,
    message = "We could not log you in. Please try again later or use a different identity provider.",
  ) {
    super(message);
    this.name = "ServiceUnavailable";
    this.credentials = credentials;
  }
}
class TooManyRequests extends Error {
  type = "TooManyRequests" as const;

  credentials: Credentials;

  constructor(
    credentials: Credentials,
    message = "Too many failed login requests. Please try again later.",
  ) {
    super(message);
    this.name = "TooManyRequests";
    this.credentials = credentials;
  }
}
class TwoFactorRequired extends Error {
  type = "TwoFactorRequired" as const;

  credentials: Credentials;

  challenge: TwoFactorChallenge;

  constructor(
    credentials: Credentials,
    challenge: TwoFactorChallenge,
    message = "Enter the verification code we sent you to finish signing in.",
  ) {
    super(message);
    this.name = "TwoFactorRequired";
    this.credentials = credentials;
    this.challenge = challenge;
  }
}

class Unknown extends Error {
  type: "Unknown" = "Unknown";

  credentials: Credentials;

  constructor(
    credentials: Credentials,
    message = "Something went wrong, but we're not sure what. We suggest trying again or contacting help@grailed.com if the problem continues.",
  ) {
    super(message);
    this.name = "Unknown";
    this.credentials = credentials;
  }
}
export type SessionCreateError =
  | UserNotFound
  | OneTimePasswordRequired
  | TwoFactorRequired
  | Banned
  | InvalidCredentials
  | NeedsPasswordReset
  | ServiceUnavailable
  | TooManyRequests
  | Unknown;

const unpack =
  (credentials: Credentials) =>
  (res: Response): AuthenticationData => {
    if (res.data.status === "two_factor_required") {
      const nextAction = parseNextAction(res.data.nextAction);

      if (nextAction?.type !== "two_factor_challenge") {
        throw new Unknown(credentials);
      }

      throw new TwoFactorRequired(credentials, nextAction.twoFactorChallenge);
    }

    const { redirectTo, token, user } = res.data;

    if (token === null || user === null) {
      throw new Unknown(credentials);
    }

    return { redirectTo, token, user, credentials };
  };

const create = (credentials: Credentials): Promise<AuthenticationData> =>
  post<Response>(path(credentials), credentials)
    .then(unpack(credentials))
    .catch((reason: unknown) => {
      if (reason instanceof TwoFactorRequired || reason instanceof Unknown) {
        throw reason;
      }

      if (typeof reason === "string") {
        throw new Unknown(credentials, reason);
      }

      if (!(reason instanceof GrailedAPIError)) {
        if (reason instanceof Error) {
          throw new Unknown(credentials, reason.message);
        }

        throw new Unknown(credentials);
      }

      if (reason.response.status === 429) {
        throw new TooManyRequests(credentials);
      }

      const { error } = reason.body;

      if (!error) {
        throw new Unknown(credentials);
      }

      // NOTE: For this endpoint, we assume that the error is our deprecated GrailedError, which
      // has an error_id. If we're wrong, the default case will make sure things aren't too broken.

      switch (error.error_id) {
        case "not_found":
          throw new UserNotFound(credentials);

        case "otp_required":
          throw new OneTimePasswordRequired(credentials);

        case "unauthorized":
        case "forbidden":
          throw new InvalidCredentials(credentials, error.message);

        case "banned":
          throw new Banned(credentials);

        case "needs_password_reset":
          throw new NeedsPasswordReset(credentials, error.message);

        case "service_unavailable":
          throw new ServiceUnavailable(credentials, error.message);

        default:
          throw new Unknown(credentials, error.message);
      }
    });

export default create;
